import { Button } from '@mui/material';
import { Copy, DotsSixVertical, XCircle } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNotificationStore } from '../../../../store/storeNotifications';
import { INode, useFormBuilder } from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { getNodesForPage, getPageById } from '../../utils/pageUtils';
import { ImageWidget } from '../Widget/Image';
import { InputWidget } from '../Widget/Input';
import { ListWidget } from '../Widget/List';
import { OutputWidget } from '../Widget/Output';
import { ShortTextWidget } from '../Widget/ShortText';
import { SubmitWidget } from '../Widget/Submit';
import { TitleWidget } from '../Widget/Title';
import './styles.css';

const componentsRegistry = {
  title: TitleWidget,
  shortText: ShortTextWidget,
  image: ImageWidget,
  list: ListWidget,
  input: InputWidget,
  output: OutputWidget,
  submit: SubmitWidget,
};

function GridItem({ node }: { node: INode }) {
  const Component = componentsRegistry[node.type];
  if (!Component) return <></>;

  return <Component node={node} />;
}

const ResponsiveReactGridLayout = WidthProvider(GridLayout) as any;

export function FormContainer() {
  const {
    state,
    dispatch,
    setSelectNode,
    setSettingsFormMode,
    selectNode,
    currentPageId,
  } = useFormBuilder();
  const setNotification = useNotificationStore(state => state.setNotification);
  const isInitialLoad = useRef(true);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function removeWidget(widgetId: string) {
    if (state.form.newFormNodes.find(node => node.id === widgetId)) {
      setSelectNode(null);
    }
    dispatch({ type: ActionTypes.REMOVE_NODES, payload: widgetId });
  }

  function duplicateWidget(node: INode) {
    dispatch({
      type: ActionTypes.DUPLICATE_NODE,
      payload: { node, currentPageId },
    });
    setNotification({
      type: 'success',
      message: 'Node duplicated successfully',
    });
  }


  const currentPageNodes = useMemo(() => {
    if (!state.form.pages || state.form.pages.length === 0) {
      return state.form.newFormNodes;
    }

    if (!currentPageId) {
      return [];
    }

    const currentPage = getPageById(
      currentPageId,
      state.form.pages
    );

    return getNodesForPage(currentPage, state.form.newFormNodes);
  }, [
    state.form.pages,
    currentPageId,
    state.form.newFormNodes,
  ]);

  const handleLayoutChange = (currentLayout: GridLayout.Layout[]) => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    const currentPageNodeIds = new Set(
      currentPageNodes.map(node => node.id)
    );

    currentLayout.forEach(layout => {
      if (currentPageNodeIds.has(layout.i)) {
        const currentLayout = {
          i: layout.i,
          x: layout.x,
          y: layout.y,
          w: layout.w,
          h: layout.h,
          minW: layout.minW,
          minH: layout.minH,
        };

        dispatch({
          type: ActionTypes.UPDATE_NODES_LAYOUT,
          payload: { id: layout.i, updates: { layout: currentLayout } },
        });
      }
    });
  };

  const formStyle = state.form.formStyle;
  const previousNodesCount = useRef(state.form.newFormNodes.length);

  useEffect(() => {
    const currentNodesCount = state.form.newFormNodes.length;
    if (currentNodesCount > previousNodesCount.current) {
      const lastNode = state.form.newFormNodes[currentNodesCount - 1];
      if (lastNode) {
        setSelectNode(lastNode);
        setSettingsFormMode(false);
      }
    }
    previousNodesCount.current = currentNodesCount;
  }, [state.form.newFormNodes, setSelectNode, setSettingsFormMode]);

  return (
    <div
      style={{
        flex: 1,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: formStyle?.backgroundColor
          ? formStyle.backgroundColor
          : '#F9FAFB',
        backgroundImage: formStyle?.backgroundImage
          ? `url(${formStyle.backgroundImage})`
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: '8px',
        height: 'calc(100vh - 286px)',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'relative',
          minHeight: '100%',
          paddingBottom: 'calc((100vh - 215px) * 0.2)',
        }}
      >
        <ResponsiveReactGridLayout
          className="layout"
          layout={currentPageNodes.map(item => item.layout)}
          cols={12}
          rowHeight={30}
          isDraggable={true}
          isResizable={true}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
          resizeHandles={['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']}
          verticalCompact={true}
          style={{
            width: '100%',
          }}
        >
          {currentPageNodes.map(node => (
        <div
          key={node.id}
          style={{
            border:
              (hoveredNodeId === node.id || selectNode?.id === node.id) &&
              !isDragging
                ? `1px solid #3650DE`
                : `${node.data.styles.borderWeight}px solid ${node.data.styles.borderColor}`,
            borderRadius: node.data.styles.borderRadius,
            backgroundColor: node.data.styles.backgroundColor,
            boxSizing: 'border-box',
            position: 'relative',
            cursor: 'pointer',
            boxShadow:
              (hoveredNodeId === node.id || selectNode?.id === node.id) &&
              !isDragging
                ? `0 8px 25px rgba(0, 0, 0, 0.1), 0 4px 5px rgba(0, 0, 0, 0.1), inset 0 0 0 ${node.data.styles.borderWeight}px ${node.data.styles.borderColor}`
                : 'none',
            transform:
              (hoveredNodeId === node.id || selectNode?.id === node.id) &&
              !isDragging
                ? 'translateY(-2px)'
                : 'translateY(0)',
          }}
          onClick={() => {
            setSelectNode(node);
            setSettingsFormMode(false);
          }}
          onMouseDown={e => e.stopPropagation()}
          onMouseEnter={() => setHoveredNodeId(node.id)}
          onMouseLeave={() => setHoveredNodeId(null)}
        >
          <Button
            className="drag-handle"
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              zIndex: 10,
              cursor: 'grab',
              userSelect: 'none',
              width: '20px',
              height: '20px',
              minWidth: '20px',
              minHeight: '20px',
              padding: '0px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              transition: isDragging ? 'none' : 'opacity 0.2s ease-in-out',
              opacity: hoveredNodeId === node.id ? 1 : 0,
            }}
          >
            <DotsSixVertical size={20} weight="bold" color="#101828" />
          </Button>

          <div
            style={{
              width: '100%',
              height: '100%',
              padding: `${
                node.data.styles.padding !== undefined &&
                node.data.styles.padding !== null
                  ? node.data.styles.padding
                  : 16
              }px`,
              boxSizing: 'border-box',
              transition: 'padding 0.2s ease-in-out',
              display: 'flex',
              alignItems: node.data.styles.verticalAlign,
            }}
          >
            <GridItem node={node} />
          </div>

          <div
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 10,
              display: 'flex',
              gap: '8px',
              opacity: hoveredNodeId === node.id ? 1 : 0,
              transition: isDragging ? 'none' : 'opacity 0.2s ease-in-out',
            }}
          >
            <Button
              onClick={e => {
                e.stopPropagation();
                duplicateWidget(node);
              }}
              onMouseDown={e => e.stopPropagation()}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                width: '20px',
                height: '20px',
                minWidth: '20px',
                minHeight: '20px',
                padding: '0px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <Copy size={20} color="#101828" />
            </Button>

            <Button
              onClick={e => {
                e.stopPropagation();
                removeWidget(node.id);
                setSettingsFormMode(true);
              }}
              onMouseDown={e => e.stopPropagation()}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                width: '20px',
                height: '20px',
                minWidth: '20px',
                minHeight: '20px',
                padding: '0px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <XCircle size={20} color="#101828" />
            </Button>
          </div>
        </div>
      ))}
        </ResponsiveReactGridLayout>
      </div>
    </div>
  );
}
